import type { CollectionFields } from '@dovefiniscono/shared';
import type { LocalRecord } from '@dovefiniscono/sync';
import { addDays, fromISODate, joinInstant, toISODate } from '../lib/dates';

// Recurrences: rent every month, the gym every Monday, a meeting every two weeks. Each copy is an
// ordinary record with an id made from the recurrence and the day, so two devices that make it at
// once make one; `generatedUntil` remembers how far copies were made, so a deleted copy stays
// deleted.

export type Recurrence = LocalRecord<'recurrences'>;
export type RecurrenceFields = CollectionFields['recurrences'];
export type Frequency = RecurrenceFields['frequency'];
type Rule = Pick<RecurrenceFields, 'frequency' | 'interval' | 'startOn' | 'endOn'>;

/** How far ahead copies are made: movements on their day, cards a week ahead, events two months. */
export const HORIZON_DAYS: Record<RecurrenceFields['kind'], number> = {
  transaction: 0,
  task: 6,
  event: 60,
};

/** Bounded, so a recurrence every day for years cannot freeze the page. */
const MAX_COPIES = 400;

const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

/** The n-th day of a recurrence (n = 0 is its start), months and years kept on the same day. */
export function nthDate(rule: Rule, n: number): string {
  const start = fromISODate(rule.startOn);
  switch (rule.frequency) {
    case 'daily':
      return addDays(rule.startOn, n * rule.interval);
    case 'weekly':
      return addDays(rule.startOn, n * 7 * rule.interval);
    case 'monthly':
    case 'yearly': {
      const months = (rule.frequency === 'monthly' ? 1 : 12) * rule.interval * n;
      const index = start.getMonth() + months;
      const year = start.getFullYear() + Math.floor(index / 12);
      const month = ((index % 12) + 12) % 12;
      // The 31st falls on the last day of shorter months, 29 February on the 28th.
      const day = Math.min(start.getDate(), daysInMonth(year, month));
      return toISODate(new Date(year, month, day));
    }
  }
}

/** Days of a recurrence after `after` (excluded) and up to `until` (included). */
export function datesBetween(rule: Rule, after: string | null, until: string): string[] {
  const dates: string[] = [];
  for (let n = 0; n < 100_000 && dates.length < MAX_COPIES; n++) {
    const day = nthDate(rule, n);
    if (day > until || (rule.endOn !== null && day > rule.endOn)) break;
    if (after === null || day > after) dates.push(day);
  }
  return dates;
}

/** The next day of a recurrence from `from` (included), or null when it has ended. */
export function nextDate(rule: Rule, from: string): string | null {
  for (let n = 0; n < 100_000; n++) {
    const day = nthDate(rule, n);
    if (rule.endOn !== null && day > rule.endOn) return null;
    if (day >= from) return day;
  }
  return null;
}

const WEEKDAY = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MONTH = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long' });

/** "Ogni mese, il 5", "Ogni 2 settimane, il lunedì": a recurrence in words. */
export function describe(rule: Rule): string {
  const start = fromISODate(rule.startOn);
  const every = (one: string, many: string) =>
    rule.interval === 1 ? `Ogni ${one}` : `Ogni ${rule.interval} ${many}`;
  switch (rule.frequency) {
    case 'daily':
      return rule.interval === 1 ? 'Ogni giorno' : `Ogni ${rule.interval} giorni`;
    case 'weekly':
      return `${every('settimana', 'settimane')}, il ${WEEKDAY[start.getDay()]}`;
    case 'monthly':
      return `${every('mese', 'mesi')}, il ${start.getDate()}`;
    case 'yearly':
      return `${every('anno', 'anni')}, il ${MONTH.format(start)}`;
  }
}

export const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'daily', label: 'Ogni giorno' },
  { value: 'weekly', label: 'Ogni settimana' },
  { value: 'monthly', label: 'Ogni mese' },
  { value: 'yearly', label: 'Ogni anno' },
];

type Copy =
  | { collection: 'transactions'; fields: CollectionFields['transactions'] }
  | { collection: 'tasks'; fields: CollectionFields['tasks'] }
  | { collection: 'events'; fields: CollectionFields['events'] };

/** The record a recurrence makes on a day, or null when its template is incomplete. */
export function copyFor(recurrence: RecurrenceFields, day: string, position: number): Copy | null {
  const t = recurrence.template;
  if (recurrence.kind === 'transaction') {
    if (!t.accountId || !t.amount) return null;
    return {
      collection: 'transactions',
      fields: {
        accountId: t.accountId,
        categoryId: t.categoryId ?? null,
        amount: t.amount,
        occurredOn: day,
        note: t.note ?? '',
        method: t.method ?? null,
        time: t.time ?? null,
      },
    };
  }
  if (recurrence.kind === 'task') {
    if (!t.title) return null;
    return {
      collection: 'tasks',
      fields: {
        listId: t.listId ?? null,
        title: t.title,
        notes: t.notes ?? '',
        dueDate: day,
        priority: t.priority ?? 0,
        completedAt: null,
        position,
        labelIds: t.labelIds ?? null,
        checklist: t.checklist ? t.checklist.map((item) => ({ ...item, done: false })) : null,
      },
    };
  }
  if (!t.title) return null;
  const allDay = t.allDay ?? false;
  const startsAt = joinInstant(day, allDay ? '00:00' : (t.time ?? '09:00'));
  const endsAt = allDay
    ? joinInstant(addDays(day, 1), '00:00')
    : new Date(Date.parse(startsAt) + (t.minutes ?? 60) * 60_000).toISOString();
  return {
    collection: 'events',
    fields: {
      title: t.title,
      notes: t.notes ?? '',
      allDay,
      startsAt,
      endsAt,
      taskId: t.taskId ?? null,
    },
  };
}

/** What a recurrence still has to make by `today`, and how far it will then have gone. */
export function due(recurrence: RecurrenceFields, today: string) {
  if (recurrence.paused) return { dates: [], until: recurrence.generatedUntil };
  const until = addDays(today, HORIZON_DAYS[recurrence.kind]);
  const dates = datesBetween(recurrence, recurrence.generatedUntil, until);
  return { dates, until: dates.length > 0 ? (dates.at(-1) as string) : recurrence.generatedUntil };
}
